import { useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { useCorrectPolicy, usePolicySchema, usePreviewPolicyCorrection } from '../api/policies';
import {
  coercePayload,
  payloadFormOf,
  payloadSchemaOf,
  payloadValues,
} from '../model/payload-form';
import {
  editableRoles,
  hasPartnerRefusal,
  linksOf,
  partnerRefusalOf,
  seedPartnerValues,
  type PartnerFormValues,
} from '../model/partners';
import { isMember } from '../model/types';
import { ConflictNotice } from './ConflictNotice';
import { PartnerSection } from './PartnerSection';
import { PayloadFields } from './PayloadFields';

/**
 * Re-stating history, with the consequences shown first.
 *
 * A correction is not an edit. It SUPERSEDES the beliefs that covered a business-time range and
 * inserts corrected ones in their place — typically a head/corrected/tail split — in one
 * transaction, leaving `business_version` where it was (decisions S-7/S-8). The superseded rows
 * are kept forever, because GoBD and premium reproducibility need every historical belief
 * reconstructable.
 *
 * Two things follow, and both are in this dialog rather than in a comment:
 *
 *  - `reason` is REQUIRED. A correction re-states what was true; who did it and why is the audit
 *    record, and a blank one would leave a re-statement nobody can account for.
 *  - the PREVIEW is a step, not a link. A premium-relevant correction emits an event and billing
 *    raises an append-only adjustment memo against money that has already been invoiced. An
 *    operator has to see the segments and the delta BEFORE they agree to that, so `:preview` runs
 *    first and the confirm button does not exist until it has answered.
 */
export function CorrectDialog({
  code,
  policy,
  open,
  onClose,
}: {
  readonly code: string;
  readonly policy: InstanceApi.Policy | undefined;
  readonly open: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const schema = usePolicySchema(code);
  const preview = usePreviewPolicyCorrection();
  const correct = useCorrectPolicy();

  const form = useMemo(() => payloadFormOf(schema.data?.schema), [schema.data?.schema]);
  const validator = useMemo(() => payloadSchemaOf(form), [form]);
  const member = isMember(policy);
  const rules = useMemo(
    () => editableRoles(schema.data?.partnerRoles, member),
    [schema.data?.partnerRoles, member],
  );
  const [partners, setPartners] = useState<PartnerFormValues>({});

  const [step, setStep] = useState<0 | 1>(0);
  const [validFrom, setValidFrom] = useState('');
  const [validTo, setValidTo] = useState('');
  const [reason, setReason] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [seeded, setSeeded] = useState(false);

  if (!seeded && schema.data !== undefined && policy?.data !== undefined) {
    setValues(payloadValues(policy.data));
    setPartners(
      seedPartnerValues(
        rules,
        (policy.partners ?? []).map((partner) => partner.link ?? {}),
      ),
    );
    setSeeded(true);
  }
  const refusal = partnerRefusalOf(preview.error ?? correct.error, rules, partners);

  const parsed = validator.safeParse(coercePayload(form, values));
  const missing = {
    validFrom: validFrom === '',
    reason: reason.trim() === '',
  };
  const incomplete = missing.validFrom || missing.reason || !parsed.success;

  const close = (): void => {
    setStep(0);
    setSubmitted(false);
    setSeeded(false);
    setValidFrom('');
    setValidTo('');
    setReason('');
    preview.reset();
    correct.reset();
    onClose();
  };

  const variables = {
    code,
    validFrom,
    ...(validTo === '' ? {} : { validTo }),
    reason: reason.trim(),
    changes: parsed.success ? parsed.data : {},
    // The COMPLETE list the corrected segments carry (P-7).
    partners: linksOf(rules, partners),
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="md">
      <DialogTitle>{t('policies.correct.title')}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ my: 2 }}>
          <Step>
            <StepLabel>{t('policies.correct.steps.form')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('policies.correct.steps.preview')}</StepLabel>
          </Step>
        </Stepper>

        <ConflictNotice conflict={preview.conflict ?? correct.conflict} />

        {step === 0 ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">{t('policies.correct.description')}</Alert>

            <Stack direction="row" spacing={2}>
              <TextField
                type="date"
                label={t('policies.correct.validFrom')}
                value={validFrom}
                required
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(event) => {
                  setValidFrom(event.target.value);
                }}
                error={submitted && missing.validFrom}
                helperText={
                  submitted && missing.validFrom ? t('policies.endorse.errors.required') : undefined
                }
              />
              <TextField
                type="date"
                label={t('policies.correct.validTo')}
                value={validTo}
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(event) => {
                  setValidTo(event.target.value);
                }}
                helperText={t('policies.correct.validToHint')}
              />
            </Stack>

            <TextField
              label={t('policies.correct.reason')}
              value={reason}
              required
              fullWidth
              multiline
              minRows={2}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              error={submitted && missing.reason}
              helperText={
                submitted && missing.reason
                  ? t('policies.endorse.errors.required')
                  : t('policies.correct.reasonHint')
              }
            />

            {schema.isPending ? (
              <Skeleton variant="rounded" height={200} />
            ) : (
              <>
                <PayloadFields
                  form={form}
                  values={values}
                  errors={
                    submitted && !parsed.success
                      ? { [form.fields[0]?.name ?? '']: t('policies.endorse.errors.invalid') }
                      : {}
                  }
                  onChange={(name, value) => {
                    setValues((current) => ({ ...current, [name]: value }));
                  }}
                />
                <PartnerSection
                  rules={rules}
                  values={partners}
                  member={member}
                  refusal={refusal}
                  onChange={(roleErn, ids) => {
                    setPartners((current) => ({ ...current, [roleErn]: ids }));
                  }}
                />
              </>
            )}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ pt: 1 }} data-testid="correction-preview">
            {preview.isPending && (
              <Typography variant="body2">{t('policies.correct.previewRunning')}</Typography>
            )}
            {preview.data !== undefined && <PreviewResult result={preview.data} />}
            {preview.isError && preview.conflict === null && !hasPartnerRefusal(refusal) && (
              <Alert severity="error">{preview.error.message}</Alert>
            )}
            {hasPartnerRefusal(refusal) && (
              <Alert severity="error" data-testid="partner-refusal">
                {t('policies.partners.refused')} {refusal.general.join(' · ')}
              </Alert>
            )}
            {correct.isSuccess && (
              <Alert severity="success">
                <AlertTitle>{t('policies.correct.applied')}</AlertTitle>
                {/* The memo, named. Billing never rewrites an invoice — it appends, and this is
                    the record that says so (decision S-6). */}
                {t('policies.correct.memo', { ref: correct.data.adjustmentMemoRef ?? '' })}
              </Alert>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={close}>{t('policies.actions.cancel')}</Button>
        {step === 0 ? (
          <Button
            variant="contained"
            disabled={schema.isPending}
            onClick={() => {
              setSubmitted(true);
              if (incomplete) return;
              setStep(1);
              preview.mutate(variables);
            }}
          >
            {t('policies.correct.runPreview')}
          </Button>
        ) : (
          <>
            <Button
              onClick={() => {
                setStep(0);
                preview.reset();
                correct.reset();
              }}
            >
              {t('policies.actions.back')}
            </Button>
            <Button
              variant="contained"
              color="warning"
              disabled={preview.data === undefined || correct.isPending || correct.isSuccess}
              onClick={() => {
                correct.mutate(variables);
              }}
            >
              {t('policies.correct.submit')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** The dry run's answer: what would be superseded, what would replace it, and the money. */
function PreviewResult({
  result,
}: {
  readonly result: InstanceApi.PreviewPolicyCorrectionResponse;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const superseded = result.supersededSegments ?? [];
  const inserted = result.newSegments ?? [];
  const delta = result.premiumDelta;

  return (
    <Stack spacing={2}>
      <Alert severity="warning">
        <AlertTitle>{t('policies.correct.preview.title')}</AlertTitle>
        {t('policies.correct.preview.description', {
          superseded: superseded.length,
          inserted: inserted.length,
        })}
      </Alert>

      <Stack spacing={0.5}>
        <Typography variant="subtitle2" component="span">
          {t('policies.correct.preview.segments')}
        </Typography>
        {inserted.map((segment, index) => (
          <Typography key={`${segment.snapshot?.validFrom ?? ''}-${String(index)}`} variant="body2">
            {t('policies.correct.preview.segment', {
              from: segment.snapshot?.validFrom ?? '',
              to: segment.snapshot?.validTo ?? '—',
            })}
          </Typography>
        ))}
      </Stack>

      {delta !== undefined && (
        <Alert severity={delta.amount?.startsWith('-') === true ? 'info' : 'warning'}>
          {/* A decimal string, printed as it arrived. A premium is money and float64 is not. */}
          {t('policies.correct.preview.delta', {
            amount: delta.amount ?? '0',
            currency: delta.currency ?? '',
          })}
        </Alert>
      )}
    </Stack>
  );
}
